/* eslint no-param-reassign: 0 */
import {
  CustomOAuthMessages,
  OAUTH_SESSION_STORAGE_KEY,
  PASSWORD_TR,
  PENDING_OAUTH_CONFIG_UPDATE,
  SCIM_PASSWORD_SYNC_TR,
  ScimMessages,
  authenticationMethods,
  defaultConstantAttributes,
  defaultDatabaseAttributes,
  defaultGroupAttributes,
  defaultIncludeGroups,
  idmTestConnectionFailures,
  mappingDirection,
  parameterLocationOptions,
  scimConnectionTypes,
  userSchemaAttributes,
  verifyTypes,
} from 'client/util/Constants/ApplicationConstants';
import {
  assignIn,
  cloneDeep,
  extend,
  get,
  isEqual,
  isMatch,
  isPlainObject,
  set,
} from 'lodash';
import { hasPasswordSyncSupport, isOIDC } from 'client/applications/utils/ApplicationUtils';
import { notify } from 'client/util/index';
import Api from 'client/api/v2/Api';
import ApplicationApi from 'client/api/v2/ApplicationApi';
import ApplicationTemplateApi from 'client/api/v2/ApplicationTemplateApi';
import Constants, {
  DirectoryGroupingConsts,
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import SessionStorageService from 'client/util/SessionStorageService';
import SsoApi from 'client/api/v2/SsoApi';
import Util from 'client/util/Util';
import Vue from 'vue';
import axios from 'axios';
import createModel from 'client/store/modules/models/createModel';

const ScimUserImportCollection = StoreModuleMap[ItemTypeConsts.scimUserImport];
const { groupTypes, memberTypes } = DirectoryGroupingConsts;
const { error, success } = Constants;
const { SCIM, CUSTOM } = scimConnectionTypes;
const {
  APIKEY, BEARER, OAUTH2, SIMPLE_TOKEN, PASSWORD_TOKEN,
} = authenticationMethods;
const { BIDIRECTIONAL } = mappingDirection;
const { BODY } = parameterLocationOptions;

const ENDPOINTS = {
  ACTIVATE: 'ACTIVATE',
  VERIFY: 'VERIFY',
};

// Helper functions

/**
 * Recursive function to extract json paths from objects
 * @param {Object} obj base Object
 * @param {String} currentPath used to recursively concatenate object keys
 * @param {Array} pathCollection used to save and return the paths collection
 * @returns {Array} the collection of paths
 */
export function extractJsonPaths(obj, currentPath = '', pathCollection = []) {
  if (isPlainObject(obj)) {
    const keys = Object.keys(obj);
    keys.forEach((key) => {
      const newPath = `${currentPath}${currentPath !== '' ? '.' : ''}${key}`;
      extractJsonPaths(obj[key], newPath, pathCollection);
    });
  } else {
    pathCollection.push(currentPath);
  }

  return pathCollection;
}

/**
 * Format custom REST API attribute mappings to match
 * BE structure
 *
 * * _spAttribute_ -> external
 *
 * * _jcAttribute_ -> internal
 *
 * * _direction_ stays the same
 * @param {Object} mappings
 * @param {String} mappings.spAttribute
 * @param {String} mappings.jcAttribute
 * @param {Number} mappings.direction
 */
export function formatCustomApiAttributeMappings(mappings) {
  const validMappings = mappings.filter(attr => attr.spAttribute && attr.jcAttribute);
  return validMappings.map(mapping => {
    const {
      spAttribute,
      jcAttribute,
      direction,
    } = mapping;

    return {
      external: {
        value: spAttribute,
        type: 'path',
      },
      internal: {
        value: jcAttribute,
        type: 'path',
      },
      direction,
    };
  });
}

/**
 * Determines if reported failures are considered test connection failures
 * @param {String[]} currentFailures Array of the reported failures from test connection
 * @returns {Boolean} Boolean representing whether or not a test connection failure was detected
 */
export function isIdmTestConnectionFailure(currentFailures) {
  const result = idmTestConnectionFailures.find(failure => currentFailures.includes(failure));
  return !!result;
}

export function formatScimTranslationRules(rules) {
  return rules.reduce((processedRules, rule) => {
    const { target, template, exclude } = rule;
    const newRule = { target };

    if (exclude) {
      newRule.exclude = exclude;
    } else if (target !== PASSWORD_TR) {
      // When password is included, it is not sent in the payload
      newRule.template = template;
    } else {
      return processedRules;
    }
    processedRules.push(newRule);
    return processedRules;
  }, []);
}

/**
 * Translates total count location UI value to its BE equivalent
 * @param {Object} state
 * @returns string
 */
function translateLocationValueParameter(uiValue) {
  return (uiValue === BODY) ? 'field' : 'header';
}

export function getRequiredUserSchemaAttribute(target) {
  return userSchemaAttributes
    .find(attr => attr.category === 'required' && attr.attribute === target);
}

/**
 * @param {*} endpoint Constant, indicates payload target endpoint
 * @param {*} translationRulesPayload required data used only for the ACTIVATE endpoint
 * @returns Object Formatted payload for specified _endpoint_
 */
export function prepareScimPayload({ state }, endpoint, translationRulesPayload) {
  const { ACTIVATE, VERIFY } = ENDPOINTS;

  // Common payload props for SCIM endpoints
  const payload = {
    api_key: state.scimTokenKey || '',
    base_url: state.scimVanityUrl || null,
    groups_enabled: state.scimGroupsEnabled || false,
    scim_version: state.scimVersion !== '' ? state.scimVersion : 'v2',
    custom_email: state.scimTestUserEmail || null,
  };

  // Attach endpoint specific props

  if (endpoint === VERIFY) {
    payload.type = verifyTypes.CREDENTIALS;
  }

  if (endpoint === ACTIVATE) {
    payload.translation_rules = translationRulesPayload || [];
  }

  return payload;
}

export function getListUsersEndpointConfig(state) {
  const { listUsers } = state.customIdmEndpointsConfig;

  const {
    verb: method,
    path,
  } = listUsers;

  return { method, path };
}

export function getListUsersPaginationEndpointConfig(state) {
  const { listUsers } = state.customIdmEndpointsConfig;

  const {
    limit: count,
    skip,
  } = listUsers.pagination;

  return { count, skip };
}

export function getListUsersTotalCountEndpointConfig(state) {
  const { listUsers } = state.customIdmEndpointsConfig;

  const {
    location,
    value,
  } = listUsers.totalCount;

  return {
    type: translateLocationValueParameter(location),
    value,
  };
}

export function getListUsersResourcesEndpointConfig(state) {
  const { resourcesPath: resources } = state.customIdmEndpointsConfig.listUsers;
  return resources;
}

export function getUserSchemaConfig(state) {
  // User schema property
  const { userSchemaIdPath, inactiveStatus } = state;
  const { companyEmail, username, optionalAttributes } = state.customIdmMappingAttributes;

  const emailInternalName = getRequiredUserSchemaAttribute('email').attribute;
  const usernameInternalName = getRequiredUserSchemaAttribute('username').attribute;

  const allMappings = [
    {
      direction: BIDIRECTIONAL,
      jcAttribute: emailInternalName,
      spAttribute: companyEmail,
    },
    {
      direction: BIDIRECTIONAL,
      jcAttribute: usernameInternalName,
      spAttribute: username,
    },
    ...optionalAttributes,
  ];

  // Parsing string to array for BE request contract
  const inactiveValues = inactiveStatus.values === '' ? []
    : inactiveStatus.values.split(',').map(s => s.trim());

  // Will be payload's user_schema property
  const userSchema = {
    unique_identifier: userSchemaIdPath,
    inactive_status: { path: inactiveStatus.path, values: inactiveValues },
    mappings: formatCustomApiAttributeMappings(allMappings),
  };

  return userSchema;
}

/**
 * Returns the list users endpoint
 * Custom REST integration "base" configuration object
 *
 * A.k.a. Payload's **custom_protocol.api.users.list** object
 */
export function getBaseListUsersConfig(state) {
  const customRestApiConfig = {};

  // Base object path
  const listUsersBase = 'api.users.list';

  // ListUsers
  // Endpoint path config
  const listUsersEndpointPathConfig = getListUsersEndpointConfig(state);
  const listUsersEndpoint = `${listUsersBase}.endpoint`;
  set(customRestApiConfig, listUsersEndpoint, listUsersEndpointPathConfig);

  // Pagination config
  const listUsersPagination = `${listUsersBase}.pagination`;
  const listUsersEndpointPaginationConfig = getListUsersPaginationEndpointConfig(
    state, customRestApiConfig, listUsersBase,
  );
  set(customRestApiConfig, listUsersPagination, listUsersEndpointPaginationConfig);

  // Total count config
  const listUsersTotalCount = `${listUsersBase}.total_count`;
  const listUsersEndpointTotalCountConfig = getListUsersTotalCountEndpointConfig(
    state, customRestApiConfig, listUsersBase,
  );
  set(customRestApiConfig, listUsersTotalCount, listUsersEndpointTotalCountConfig);

  // Resources path config
  const listUsersResources = `${listUsersBase}.resources`;
  const listUsersEndpointResourcesConfig = getListUsersResourcesEndpointConfig(
    state, customRestApiConfig, listUsersBase,
  );
  set(customRestApiConfig, listUsersResources, listUsersEndpointResourcesConfig);

  return customRestApiConfig;
}

/**
 * Helper function that returns the formatted payload for
 * the endpoint
 *
 * _POST api/v2/applications/:app_id/provision/activate_
 *
 * This payload is exclusive for Custom REST OAuth integrations
 * @param {*} (ApplicationModel's) state
 */

export function getPayloadForCustomOAuth({ idmOauthConfig }) {
  const {
    authorizationUrl,
    clientId,
    clientSecret,
    accessTokenUrl,
    revokeUrl,
    scopes = [],
  } = idmOauthConfig;

  const scopesAsArray = (scopes.length === 0) ? [] : scopes.split(' ');

  const payload = {
    auth_type: OAUTH2,
    oauth: {
      auth_url: authorizationUrl,
      client_id: clientId,
      client_secret: clientSecret,
      token_url: accessTokenUrl,
      scopes: scopesAsArray,
    },
    // This last two are required by the endpoint but
    // not yet used by the BE
    groups_enabled: false,
    translation_rules: [],
  };

  if (revokeUrl.length !== 0) payload.oauth.revoke_url = revokeUrl;

  return payload;
}

/**
 * Helper function to fetch from the store state all necessary payload
 * properties for customIdmMapping BErequest
 * @param {Object} state Store model state
 */
export function prepareConfigureSCIMPayload({ state, translationRulesPayload }) {
  let payload = {
    api_key: state.scimTokenKey || '',
    base_url: state.scimVanityUrl || null,
  };

  const connectionType = state.scimConnectionType;

  if (connectionType === SCIM) {
    const { ACTIVATE } = ENDPOINTS;
    payload = prepareScimPayload({ state }, ACTIVATE, translationRulesPayload);
  }

  if (connectionType === CUSTOM) {
    const { authenticationMethod, scimIsTestConnectionPassed } = state;
    if (authenticationMethod === OAUTH2 && !scimIsTestConnectionPassed) {
      return getPayloadForCustomOAuth(state);
    }

    // Will be payload's custom_protocol property
    const customProtocol = getBaseListUsersConfig(state);
    payload.custom_protocol = customProtocol;

    payload.custom_protocol.user_schema = getUserSchemaConfig(state);

    // This get property at the time of writing is needed by the BE
    // There are plans to remove it
    const { path } = state.customIdmEndpointsConfig.listUsers;
    set(customProtocol, 'api.users.get', {
      method: 'GET',
      path: `${path}/:id`,
    });

    // Update payload token key management, if its auth method is using the header
    if (authenticationMethod === APIKEY) {
      const {
        name: headerName,
        value: headerValue,
      } = state.headerKeyPair;

      payload.api_key = headerValue;
      payload.auth_header = headerName;
      payload.auth_type = APIKEY;
    }
  }

  return payload;
}

/**
 * Helper function to handle backwards compatible payload for
 * ApplicationApiForCustomApitFeature & ApplicationApi usage
 * @param {Object} rootState Root store model state
 * @param {Object} state Store model state
 * @param {String} appId Application Id
 * @param {Object} translationRulesPayload TranslationRulesPayload
 */
export function getConfigureSCIMPayload({
  state,
  translationRulesPayload,
}) {
  return prepareConfigureSCIMPayload({ state, translationRulesPayload });
}

/**
 * Helper function to fetch from the store state all necessary payload
 * properties depending on the Idm type of connection to pass to BErequest
 * @param {Object} state Store model state
 */
export function prepareSCIMPostVerifyPayload({ state }) {
  let payload = {
    api_key: state.scimTokenKey || '',
    base_url: state.scimVanityUrl || '',
  };
  const connectionType = state.scimConnectionType;

  if (connectionType === SCIM) {
    const { VERIFY } = ENDPOINTS;
    payload = prepareScimPayload({ state }, VERIFY);
  }

  if (connectionType === CUSTOM) {
    // Will be payload's custom_protocol property
    const customProtocol = getBaseListUsersConfig(state);

    const userSchema = getUserSchemaConfig(state);

    // Payload final form
    payload = {
      ...payload,
      custom_protocol: {
        ...customProtocol,
        // request but at the time of writing this, the BE requires it
        user_schema: userSchema,
      },
    };

    // @TODO: Api key will use header key-value pair, not token key
    const authMethod = state.authenticationMethod;
    if (authMethod === APIKEY) {
      const {
        name: headerName,
        value: headerValue,
      } = state.headerKeyPair;

      payload.api_key = headerValue;
      payload.auth_header = headerName;
      payload.auth_type = APIKEY;
    }
  }

  return payload;
}

/**
 * Helper function to parse BE response customProtocol object
 * GET /provision provider_configuration.custom_protocol
 * @param {Object} customProtocol
 * @returns {Object} Object with the needed custom REST API config properties
 */
export function parseProvisionResponseCustomProtocol(customProtocol) {
  const {
    api,
    user_schema: userSchema,
  } = customProtocol;

  // Parse listUsers endpoint config
  const {
    endpoint,
    resources,
    total_count: totalCount,
    pagination,
  } = api.users.list;

  const totalCountLocationTranslation = {
    field: parameterLocationOptions.BODY,
    header: parameterLocationOptions.HEADER,
  };
  const listUsersEndpointConfig = {
    func: 'listUsers',
    verb: endpoint.method,
    path: endpoint.path,
    resourcesPath: resources,
    totalCount: {
      location: totalCountLocationTranslation[totalCount.type],
      value: totalCount.value,
    },
    pagination: {
      limit: pagination.count,
      skip: pagination.skip,
    },
  };

  // Parse userSchema mappings
  const {
    unique_identifier: userSchemaIdPath,
    // inactive_status: inactiveStatus,
    inactive_status: inactiveStatus = { path: '', values: [] },
  } = userSchema;

  /**
   * Transform BE formatted attribute mapping to use
   * AdminPortal key names
   * @param {Object} mapping
   * @param {String} mapping.direction
   * @param {String} mapping.external
   * @param {String} mapping.internal
   * @returns {Object} { spAttribute, jcAttribute, direction }
   */
  const parseMapping = (mapping) => {
    const { direction, external, internal } = mapping;
    return {
      spAttribute: external.value,
      jcAttribute: internal.value,
      direction,
    };
  };

  // Separate attributes in required and optional
  // Array of objects because currently the app store and BE's response
  // use different names for the required fields,
  // plan is to sync them in the future
  const requiredAttrs = [
    {
      nameInResponse: 'email',
      nameInStoreModel: 'companyEmail',
    },
    {
      nameInResponse: 'username',
      nameInStoreModel: 'username',
    }];

  const optionalAttributes = [];
  const requiredAttributes = {};

  userSchema.mappings.forEach(mapping => {
    const parsedMapping = parseMapping(mapping);
    const isRequiredMapping = requiredAttrs.map(attr => attr.nameInResponse)
      .includes(parsedMapping.jcAttribute);

    if (isRequiredMapping) {
      const {
        spAttribute: value,
        jcAttribute, // Can be either [email | username]
      } = parsedMapping;

      // Get the name used in app store model to use as key
      const key = requiredAttrs
        .find(attr => attr.nameInResponse === jcAttribute).nameInStoreModel;

      requiredAttributes[key] = value;
    } else {
      optionalAttributes.push(parsedMapping);
    }
  });

  // Parsing BE response array to string to handle Input's state
  inactiveStatus.values = (Array.isArray(inactiveStatus.values)) ? inactiveStatus.values.join() : '';

  return {
    listUsersEndpointConfig,
    optionalAttributes,
    requiredAttributes,
    userSchemaIdPath,
    inactiveStatus,
  };
}

/**
 * Returns an object that is a compilation of all relevant properties from
 * the state in order to save them to session storage meanwhile OAuth auth flow
 * is taking place
 * @param {Object} state destructuring target
 * @returns {Object} object to save to session storage
 */
export function pickStatePropertiesForSessionStorage({
  authenticationMethod,
  scimConnectionType,
  scimVanityUrl,
  idmOauthConfig,
  userSchemaIdPath,
  customIdmEndpointsConfig,
  customIdmMappingAttributes,
}) {
  // Remove sensitive data
  const customIdmOauthConfigClean = cloneDeep(idmOauthConfig);
  delete customIdmOauthConfigClean.clientId;
  delete customIdmOauthConfigClean.clientSecret;

  return {
    authenticationMethod,
    baseUrl: scimVanityUrl,
    customIdmEndpointsConfig,
    customIdmMappingAttributes,
    idmOauthConfig: customIdmOauthConfigClean,
    scimConnectionType,
    userSchemaIdPath,
  };
}

/**
 * Save passed object stringified to session storage
 * @param {Object} obj Object to save on session storage
 */
export function saveStateToSessionStorage(obj, sessionStorageKey) {
  SessionStorageService.setItem(
    sessionStorageKey,
    JSON.stringify(obj),
  );
}

/**
 * Set test config errors on state
 * @param {*} commit state.commit
 * @param {*} failures Failures object to add
 */
export function setFailures({ commit }, failures = {}) {
  const failuresList = Object.keys(failures)
    .map(key => ({ errorType: key, message: failures[key].message }));
  commit('SET_SCIM_TEST_CONFIG_ERRORS', failuresList);
}

/**
 * Returns object saved to session storage and delete it after reading it
 * @param {String} key
 * @returns {Object|null} Object with config before going to OAuth auth or null
 */
export function consumeStateFromSessionStorage(key) {
  const retrievedConfig = SessionStorageService.getItem(key);
  SessionStorageService.removeItem(key);
  return JSON.parse(retrievedConfig);
}

/**
 * Call the necessary mutations to set data back to model using
 * the read config from the Session Storage
 * @param {Object} config Object coming from the Session Storage
 * @param {Object} commit Destructured from passed context
 * @param {boolean} setAsCurrentConfig Indicates wether to set config
 * as "old" config for change detection, introduced by Custom REST OAuth integration
 * specifically coming back to the AP after consent page
 */
export function loadStateFromSessionStorage({ commit }, config, setAsCurrentConfig = true) {
  // Read config and set values on app model
  const {
    authenticationMethod,
    baseUrl,
    scimConnectionType,
    idmOauthConfig,
    userSchemaIdPath,
    customIdmEndpointsConfig,
    customIdmMappingAttributes,
  } = config;

  // Set API type and authentication method
  commit('SET_AUTH_METHOD', authenticationMethod);
  commit('SET_SCIM_CONNECTION_TYPE', scimConnectionType);

  // Set Base URL
  commit('SET_SCIM_VANITY_URL', baseUrl);

  // Set OAuth config
  // Add back default sensitive data
  idmOauthConfig.clientId = '';
  idmOauthConfig.clientSecret = '';
  idmOauthConfig.clientNameSpace = '';
  idmOauthConfig.username = '';
  idmOauthConfig.password = '';
  idmOauthConfig.host = '';
  idmOauthConfig.passwordAccessTokenUrl = '';
  commit('SET_OAUTH_CONFIG_VALUE', idmOauthConfig);

  // Set custom protocol configuration
  commit('SET_USER_SCHEMA_ID_PATH', userSchemaIdPath);

  // Pass it spread due to actual param being on customIdmEndpointsConfig.listUsers
  commit('UPDATE_CUSTOM_IDM_ENDPOINT', {
    func: 'listUsers', // Add func key to make it compatible with mutation
    ...customIdmEndpointsConfig.listUsers,
  });

  // Set attribute mappings
  commit('SET_CUSTOM_IDM_MAPPING_ATTRIBUTES', customIdmMappingAttributes);

  if (setAsCurrentConfig) {
    // Save state as "old config"
    commit('SET_OLD_CUSTOM_API_CONFIG');
  }
}

// Store model set up functions
export const getDefaultItem = () => ({
  active: false,
  beta: false,
  color: undefined,
  config: {},
  databaseAttributes: [],
  description: '',
  displayLabel: '',
  displayName: '',
  id: '',
  isBookmark: false,
  isCustomLogo: false,
  keywords: '',
  learnMore: '',
  logo: null,
  name: '',
  provision: '',
  sso: {},
  ssoUrl: '',
  type: ItemTypeConsts.application,
});

// TODO: Some objects will be extracted to separate SCIM and Custom API
// [Candidate] - Candidate property to me extracted from this object
export const getScimDefault = () => ({
  authenticationMethod: BEARER, // [Candidate]
  headerKeyPair: { // [Candidate]
    name: '',
    value: '',
  },
  idmNeedsSimpleToken: false,
  idmNeedsPasswordToken: false,
  userSchemaIdPath: '',
  inactiveStatus: {
    path: '',
    values: '',
  },
  customIdmMappingAttributes: {
    companyEmail: '',
    username: '',
    optionalAttributes: [],
  },
  customIdmEndpointsConfig: {
    listUsers: {
      verb: 'GET',
      path: '',
      pagination: {
        skip: '',
        limit: '',
      },
      resourcesPath: '',
      totalCount: {
        location: BODY,
        value: '',
      },
    },
  },
  idmOauthConfig: {
    authorizationUrl: '',
    clientId: '',
    clientSecret: '',
    accessTokenUrl: '',
    revokeUrl: '',
    scopes: '',
    clientNameSpace: '',
    username: '',
    password: '',
    host: '',
    passwordAccessTokenUrl: '',
  },
  isIdmOauthConfigured: false,
  isScimActive: false,
  isScimSupported: false,
  // Indicates that app has been configured as a custom IdM before it turned into a prebuilt
  isPrebuiltButCustomConfigured: false,
  oldCustomApiConfig: { // [Candidate]
    authenticationMethod: BEARER,
    headerKeyPair: {
      name: '',
      value: '',
    },
    customIdmMappingAttributes: {
      companyEmail: '',
      username: '',
      optionalAttributes: [],
    },
    customIdmEndpointsConfig: {
      listUsers: {
        verb: 'GET',
        path: '',
        pagination: {
          skip: '',
          limit: '',
        },
        resourcesPath: '',
        totalCount: {
          location: BODY,
          value: '',
        },
      },
    },
    idmOauthConfig: {
      authorizationUrl: '',
      clientId: '',
      clientSecret: '',
      accessTokenUrl: '',
      revokeUrl: '',
      scopes: '',
      clientNameSpace: '',
      username: '',
      password: '',
      host: '',
      passwordAccessTokenUrl: '',
    },
    userSchemaIdPath: '',
    inactiveStatus: {
      path: '',
      values: '',
    },
  },
  // Used to avoid modification of AsidePanel component "lifecycle"
  pendingIdmNotifications: [],
  clientId: null, // BE client ID, not to be confused with OAuth config one
  scimAuthState: '',
  scimConnectionType: SCIM,
  scimGroupsEnabled: true,
  scimGroupsSupport: false,
  scimNeedsCredentials: false,
  scimNeedsVanityUrl: false,
  scimRedirectLocation: '',
  scimTestUserEmail: '',
  scimVanityUrl: null,
  scimIsTestConnectionPassed: false,
  scimTokenKey: '',
  scimVersion: 'v2',
  scimTranslationRules: [],
  scimTestConnectionErrors: [],
  scimTestConfigErrors: [],
  scimGenericOldData: {
    scimGroupsEnabled: true,
    scimVanityUrl: null,
    scimVersion: 'v2',
    scimIsTestConnectionPassed: false,
    scimTranslationRules: [],
    scimTestUserEmail: '',
  },
});

export const getDefaultOidcClientData = () => ({
  oidcConfigResponse: {
    clientId: '',
    clientSecret: '',
  },
});

export async function getTemplateByName(appName) {
  const { results } = await ApplicationTemplateApi.fetch(0, 5, 'name', appName);
  const item = results.find(({ name }) => name === appName);

  return item;
}

export async function loadApplicationItem(id, { commit }) {
  const item = await ApplicationApi.get(id);
  const template = await getTemplateByName(item.name);

  if (isOIDC(item)) {
    const sso = await SsoApi.fetch(item._id);

    assignIn(item.sso, sso);
  }

  commit('SET_TEMPLATE', template);
  commit('SET_ITEM', cloneDeep(item));
  commit('SET_PREVIOUS_ITEM', cloneDeep(item));
  commit('SET_IS_BOOKMARK');
  commit('SET_IS_CUSTOM_LOGO');
  commit('ADD_DEFAULT_PROPERTY_VALUES');
  commit('SET_UPDATE_MODEL_PROPERTIES');
  commit('SPLIT_SSO_URL');

  return item;
}

export function setOauthConfigAfterActivation(commit, oauthResponseObject) {
  const {
    auth_url: authorizationUrl,
    token_url: accessTokenUrl,
    revoke_url: revokeUrl,
    scopes,
  } = oauthResponseObject;

  const oauthConfig = {
    authorizationUrl,
    clientId: '',
    clientSecret: '',
    clientNameSpace: '',
    username: '',
    password: '',
    host: '',
    passwordAccessTokenUrl: '',
    accessTokenUrl,
    revokeUrl,
    scopes: scopes.join(' '),
  };
  commit('SET_OAUTH_CONFIG_VALUE', oauthConfig);
  commit('SET_OLD_CUSTOM_API_CONFIG');
  commit('SET_SCIM_CONNECTION_TYPE', CUSTOM);
  commit('SET_AUTH_METHOD', OAUTH2);
  commit('SET_SCIM_TEST_CONNECTION_PASSED', true);
  commit('SET_IDM_OAUTH_CONFIGURED', true);
  commit('SET_OLD_CUSTOM_API_CONFIG');
}

/**
 *
 * @param {Function} commit - Vuex model commit function
 * @param {Object} data
 * @param {String} data.type - Auth type
 * @param {Boolean} [data.vanity_url] - Does IdM uses vanity url?
 * @param {String} [data.redirect] - oAuth consent page URL
 */
export function setAuthModelOptions(commit, data) {
  commit('SET_AUTH_METHOD', data.type);
  switch (data.type) {
    case BEARER:
      commit('SET_SCIM_NEEDS_CREDENTIALS', true);

      if (data.vanity_url) {
        commit('SET_SCIM_NEEDS_VANITY_URL', true);
      }
      break;

    case SIMPLE_TOKEN:
      commit('SET_IDM_NEEDS_SIMPLE_TOKEN', true);
      break;

    case PASSWORD_TOKEN:
      commit('SET_IDM_NEEDS_PASSWORD_TOKEN', true);
      break;

    case OAUTH2:
    default:
      commit('SET_REDIRECT_LOCATION', data.redirect);
      break;
  }
}

export async function loadApplicationScim(item, {
  state, commit, dispatch, getters,
}) {
  commit('INIT_SCIM_PROPERTIES');
  let activateResponse = {};
  let configResponse = {};
  let isScimActive;
  const passwordToken = 'password_token';

  // Required and permanent translation rules
  // Used as a "check list" to indicates which translation rules are already present
  const requiredTrs = new Map([[
    PASSWORD_TR, { translationRule: cloneDeep(SCIM_PASSWORD_SYNC_TR), exists: false },
  ]]);

  const addRequiredTrs = () => {
    // Add required translation rules that were not got from provision response
    requiredTrs.forEach(({ translationRule, exists }) => {
      if (!exists) {
        // BE does not return password so, if it does not come back then it is included
        dispatch('addScimTranslationRule', { ...translationRule, exclude: false });
      }
    });
  };

  // Call GET /provision
  try {
    activateResponse = await ApplicationApi.SCIMGetProvisionActivate(item.id);
  } catch (e) {
    // No provision, add all required translation rules
    dispatch('setDefaultScimTranslationRules');
    const beforeAuthIdMConfig = consumeStateFromSessionStorage(`${OAUTH_SESSION_STORAGE_KEY}-${item.id}`);
    // If an entry was found in this catch block means that OAuth failed since the
    // SCIMGetProvisionActivate should've returned the OAuth config on success
    if (beforeAuthIdMConfig) {
      loadStateFromSessionStorage({ commit }, beforeAuthIdMConfig);
      // Register a auth failure
      commit('ADD_PENDING_IDM_NOTIFICATION', {
        type: error,
        notificationConfig: CustomOAuthMessages.get('AUTH_FAILURE'),
      });
    }
    return;
  }

  // Call GET /config
  try {
    configResponse = await ApplicationApi.idmGetConfig(item.name);

    const { data: { disabled } } = activateResponse;

    isScimActive = !disabled;
  } catch (e) {
    commit('ADD_PENDING_IDM_NOTIFICATION', {
      type: error,
      notificationConfig: {
        message: 'Get configuration request failed',
      },
    });
    return;
  }

  const responseData = { ...activateResponse.data, ...configResponse.data };

  if (responseData?.auth?.type === passwordToken) {
    commit('SET_OAUTH_CONFIG_VALUE', { clientNameSpace: responseData.name_space, host: responseData.host, passwordAccessTokenUrl: responseData.password_access_token_url });
  }

  const response = { ...activateResponse, ...configResponse, data: responseData };

  const { data } = response;

  const {
    id: clientId,
    groups_enabled: scimGroupsEnabled = false,
    provider_configuration: spConfig = {},
    translation_rules: translationRules = [],
    groups_supported: groupsSupport,
    disabled = false,
  } = data;

  // Both pre-built and generic connector needs to set isScimActive
  // @todo Might plan to change name to a more generic one
  commit('SET_SCIM_STATUS', isScimActive);

  translationRules.forEach((rule) => {
    const { template = '', target, exclude = false } = rule;
    dispatch('addScimTranslationRule', { template, target, exclude });
    const searchedTr = requiredTrs.get(target);

    // Set exists to true to indicate translation rule existence
    if (searchedTr) { searchedTr.exists = true; }
  });

  const isGenericConnector = Object.keys(spConfig).length > 0;
  if (!isGenericConnector) {
    commit('SET_SCIM_GROUPS_ENABLE', scimGroupsEnabled);

    const authData = {
      type: data.auth.type,
      vanity_url: data.api.vanityUrl,
    };
    setAuthModelOptions(commit, authData);
    if (hasPasswordSyncSupport) {
      addRequiredTrs();
    }

    // Set correct IdM connection for prebuilt
    // provision.type possible values: [idm|scim]:<app name>
    const { type } = state.template.provision;
    const isConnectionTypeScim = type.split(':')[0] === SCIM.toLowerCase();
    const idmConnectionType = isConnectionTypeScim ? SCIM : CUSTOM;
    dispatch('setScimConnectionType', idmConnectionType);

    if (idmConnectionType === CUSTOM) {
      const { import_injection_query: importInjectionQuery = '' } = data;
      dispatch(
        `${ScimUserImportCollection}/setImportInjectionQuery`,
        importInjectionQuery,
        { root: true },
      );
      dispatch(
        `${ScimUserImportCollection}/setApplyAdvancedFilter`,
        !!importInjectionQuery,
        { root: true },
      );
    }
  } else {
    // Flag used to indicate if commit SET_OLD_CUSTOM_API_CONFIG should be called
    // Flows other than OAuth should set to true
    let shouldSetAsCurrentConfig = true;
    const {
      base_url: urlValue = '',
      scim_version: scimVersion = 'v2',
      custom_protocol: customProtocol = {},
      custom_email: scimTestUserEmail,
      custom_oauth: oauth,
    } = spConfig;
    const hasOauth = Boolean(oauth);
    const hasCustomProtocol = (Object.keys(customProtocol).length !== 0);

    const handleCustomRestApiConnector = async () => {
      if (hasOauth) {
        // Delete entry on SessionStorage if there is since BE has the OAuth config
        consumeStateFromSessionStorage(`${OAUTH_SESSION_STORAGE_KEY}-${item.id}`);
        // [[ Processing of OAuth configuration ]]
        // Poll to see if user is trying to update config
        const pendingOAuthConfigUpdate = consumeStateFromSessionStorage(
          `${PENDING_OAUTH_CONFIG_UPDATE}-${item.id}`,
        );
        // Set "old" version got from BE
        setOauthConfigAfterActivation(commit, oauth);

        // This app: has an active integration and pending updates to apply
        const shouldUpdateOauthConfig = !disabled && pendingOAuthConfigUpdate;
        if (shouldUpdateOauthConfig) {
          shouldSetAsCurrentConfig = false;

          const registerOauthUpdateSuccess = () => commit(
            'ADD_PENDING_IDM_NOTIFICATION',
            {
              type: success,
              notificationConfig: CustomOAuthMessages.get('UPDATE_SUCCESS'),
            },
          );

          const registerOauthUpdateFailure = (reason = '') => commit(
            'ADD_PENDING_IDM_NOTIFICATION',
            {
              type: error,
              notificationConfig: CustomOAuthMessages.get(reason),
            },
          );
          const customProtocolConfig = parseProvisionResponseCustomProtocol(customProtocol);
          dispatch('setCustomRestApiConfig', customProtocolConfig);
          commit('SET_OLD_CUSTOM_API_CONFIG');

          // Set new config BUT without setting it as
          // the current config to detect changes
          loadStateFromSessionStorage(
            { commit }, pendingOAuthConfigUpdate, shouldSetAsCurrentConfig,
          );

          // To know if a OAuth config update failed after reauth,
          // compare current OAuth config and the one saved on
          // the session storage.

          // If they're the same, means the new update was accepted
          // and persisted on the BE.

          const {
            // The introduction of this flag is to detect credential issues when
            // doing reauth since _didOauthConfigChanged_ is not able to detect
            // creds changes after a redirect.
            authorization_incomplete: authIncomplete,
          } = oauth;

          if (getters.didOauthConfigChanged || authIncomplete) {
            // Hard fail, don't attempt to update custom protocol config
            registerOauthUpdateFailure('AUTH_FAILURE');
          } else if (getters.didCustomApiConfigChanged) {
            try {
              await dispatch('updateConfigureScim');
              commit('SET_OLD_CUSTOM_API_CONFIG');
              registerOauthUpdateSuccess();
            } catch (err) {
              if (err.response) {
                // Has failures list set them on state
                const { failures = null } = err.response.data;
                if (failures) {
                  setFailures({ commit }, failures);
                }
              }
              registerOauthUpdateFailure('UPDATE_FAILURE');
            }
          } else {
            registerOauthUpdateSuccess();
          }
        }
      }

      // [[ Processing of custom protocol configuration ]]
      if (shouldSetAsCurrentConfig && hasCustomProtocol) {
        const customProtocolConfig = parseProvisionResponseCustomProtocol(customProtocol);
        dispatch('setCustomRestApiConfig', customProtocolConfig);
        commit('SET_OLD_CUSTOM_API_CONFIG', customProtocolConfig);
      }
    };

    // Set flag to indicate that this app is now a prebuilt app
    // but has been configured with a custom IdM configuration
    if (state.template?.provision) {
      dispatch('setIsPrebuiltButCustomConfigured', true);
    }

    const isCustomRestApiConnector = hasCustomProtocol || hasOauth;

    if (isCustomRestApiConnector) {
      await handleCustomRestApiConnector();
      dispatch('setScimConnectionType', CUSTOM);
    } else {
      // Custom SCIM connector
      addRequiredTrs();
      dispatch('setScimTestUserEmail', scimTestUserEmail);
      dispatch('setScimConnectionType', SCIM);
    }

    dispatch('setScimCredentials', {
      urlValue,
      keyValue: '',
      scimVersion,
    });

    // Common across both SCIM and Custom REST
    commit('SET_SCIM_GROUPS_SUPPORT', groupsSupport);
    if (!groupsSupport) {
      commit('SET_SCIM_GROUPS_ENABLE', false);
    } else {
      commit('SET_SCIM_GROUPS_ENABLE', scimGroupsEnabled);
    }

    if (isScimActive) {
      commit('SET_SCIM_TEST_CONNECTION_PASSED', true);
    }

    // Set client ID to be used for update operations
    commit('SET_CLIENT_ID', clientId);
    try {
      const { import_injection_query: importInjectionQuery } = data;
      dispatch(
        `${ScimUserImportCollection}/setImportInjectionQuery`,
        importInjectionQuery,
        { root: true },
      );
      dispatch(
        `${ScimUserImportCollection}/setApplyAdvancedFilter`,
        !!importInjectionQuery,
        { root: true },
      );
    } catch (e) { /* NOOP */ }
  }
  commit('SET_SCIM_GENERIC_OLD_DATA');
}

export async function loadApplicationTemplate(id, { commit, dispatch, state }) {
  const template = await ApplicationTemplateApi.get(id);
  if (template?.sso?.oidc && state?.item?.sso?.hidden) {
    // This condition will undo the hardcoded reset for the hidden field in the
    // template that interferes with the new application workflow.
    // @see https://jumpcloud.atlassian.net/browse/DE-2918
    template.sso.hidden = state.item.sso.hidden;
  }
  commit('SET_TEMPLATE_ID', id);
  commit('SET_TEMPLATE', template);
  commit('SET_ITEM', template);
  commit('SET_PREVIOUS_ITEM', template);
  commit('SET_IS_BOOKMARK');
  commit('SET_IS_CUSTOM_LOGO');
  commit('SET_SSO_URL');
  commit('ADD_DEFAULT_PROPERTY_VALUES');
  commit('SET_UPDATE_MODEL_PROPERTIES');
  commit('INIT_SCIM_PROPERTIES');
  commit('SPLIT_SSO_URL');

  if (hasPasswordSyncSupport(template.name)) {
    dispatch('setDefaultScimTranslationRules');
  }

  return template;
}

const model = createModel(getDefaultItem, 'id', ApplicationApi);

export function getDefaultState() {
  return {
    apiKey: '',
    appLogo: null,
    certOrKeyHasChanged: false,
    isDefaultLogoRestored: false,
    isItemBeingCreated: false,
    name: '',
    spEntityId: '',
    template: {},
    templateId: '',
    ...getScimDefault(),
    ...getDefaultOidcClientData(),
  };
}

export default {
  namespaced: true,

  state: {
    ...model.state,
    ...getDefaultState(),
  },

  actions: {
    ...model.actions,

    /**
     * This action is meant to be called after _configureScim_
     */
    async activateScim({ commit, state }) {
      commit('SET_SCIM_TEST_CONNECTION_ERRORS', []);
      commit('SET_SCIM_TEST_CONFIG_ERRORS', []);

      const useHeaderAuth = (state.authenticationMethod === APIKEY);
      const payload = {
        apiKey: useHeaderAuth ? state.headerKeyPair.value : state.scimTokenKey,
        appName: state.item.name,
        clientId: state.idmOauthConfig.clientId,
        clientSecret: state.idmOauthConfig.clientSecret,
        clientNameSpace: state.idmOauthConfig.clientNameSpace,
        password: state.idmOauthConfig.password,
        username: state.idmOauthConfig.username,
        host: state.idmOauthConfig.host,
        passwordAccessTokenUrl: state.idmOauthConfig.passwordAccessTokenUrl,
        groupsEnabled: state.scimGroupsEnabled,
        state: state.scimAuthState,
        vanityUrl: state.scimVanityUrl,
      };

      await ApplicationApi.SCIMPostProvisionCallback(payload);
      commit('SET_SCIM_NEEDS_CREDENTIALS', false);
      commit('SET_SCIM_AUTH_STATE', '');
      commit('SET_SCIM_STATUS', true);
      commit('SET_SCIM_TOKEN_KEY', '');
      commit('SET_OAUTH_CONFIG_VALUE', {
        clientId: '', clientSecret: '', clientNameSpace: '', username: '', password: '', host: '', passwordAccessTokenUrl: '',
      });
    },

    activateIdmWithOauth({
      commit, dispatch, state, rootState,
    }) {
      const translationRulesPayload = formatScimTranslationRules(state.scimTranslationRules);

      commit('SET_SCIM_TEST_CONNECTION_ERRORS', []);
      commit('SET_SCIM_TEST_CONFIG_ERRORS', []);

      dispatch('removeMalformedOptionalAttributes');

      const payload = getConfigureSCIMPayload({
        rootState,
        state,
        translationRulesPayload,
      });

      return ApplicationApi.SCIMPutProvision(state.item.id, payload);
    },

    addScimTranslationRule({ commit }, translationRule = {}) {
      const {
        _id = Util.getUniqueId(),
        target = '',
        template = '',
        exclude = undefined,
      } = translationRule;

      commit('ADD_SCIM_TRANSLATION_RULE', {
        _id,
        target,
        template,
        exclude,
      });
    },

    clearPendingIdmNotifications({ commit }) {
      commit('CLEAR_PENDING_IDM_NOTIFICATIONS');
    },

    configureScim({
      commit,
      dispatch,
      state,
      rootState,
    },
    { applicationId } = {}) {
      const appId = applicationId || state.item.id;
      const translationRulesPayload = formatScimTranslationRules(state.scimTranslationRules);

      commit('SET_SCIM_TEST_CONNECTION_ERRORS', []);
      commit('SET_SCIM_TEST_CONFIG_ERRORS', []);

      dispatch('removeMalformedOptionalAttributes');

      const payload = getConfigureSCIMPayload({
        rootState,
        state,
        translationRulesPayload,
      });

      if (state.authenticationMethod === OAUTH2) {
        const sessionStorageKey = `${OAUTH_SESSION_STORAGE_KEY}-${appId}`;
        dispatch('saveAppStateForAuth', sessionStorageKey);
      }

      return ApplicationApi.SCIMPostProvisionActivate(appId, payload)
        .then(({ data }) => {
          commit('SET_SCIM_AUTH_STATE', data.state);
          setAuthModelOptions(commit, { ...data, redirect: data.location });
          return data;
        })
        .then((data) => {
          if (data.type === 'oauth' && data.vanity_url) {
            let org = state.spEntityId;
            const urlRegex = /(([\w]+:)?\/\/)?(([\d\w]|%[a-fA-f\d]{2,2})+(:([\d\w]|%[a-fA-f\d]{2,2})+)?@)?([\d\w][-\d\w]{0,253}[\d\w]\.)+[\w]{2,63}(:[\d]+)?(\/([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)*(\?(&?([-+_~.\d\w]|%[a-fA-f\d]{2,2})=?)*)?(#([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)?/;
            if (!urlRegex.test(org)) {
              const urlError = new Error('SP Entity ID should be a valid URL to configure Identity Management');
              throw urlError;
            }
            org = org.split('/').pop();
            const vanityUrl = `https://api.github.com/scim/v2/organizations/${org}`;
            return ApplicationApi
              .SCIMPutProvisionActivate(state.item.id, state.scimAuthState, vanityUrl);
          }

          return data;
        })
        .catch((err) => {
          if (err.response) {
            const { failures = null } = err.response.data;
            if (failures) {
              setFailures({ commit }, failures);
            }
          } else {
            commit('SET_SCIM_GROUPS_ENABLE', false);
          }
          return Promise.reject(err);
        });
    },

    verifyScimConnection({ commit, state }, options = {}) {
      const {
        saveErrors = true,
        type = verifyTypes.CREDENTIALS,
      } = options;

      const payload = prepareSCIMPostVerifyPayload({ state });
      /**
       * Type = SCHEMA lets the /verify endpoint to return a user info
       * for the preview functionality
       */
      payload.type = type;

      // For API key-less update, use client ID
      if (!payload.api_key) {
        payload.client_id = state.clientId;
        delete payload.api_key;
      }

      // Errors clean up
      commit('SET_SCIM_TEST_CONNECTION_ERRORS', []);
      commit('SET_SCIM_TEST_CONFIG_ERRORS', []);

      const buildError = (errorType, message, category = verifyTypes.CREDENTIALS) => {
        const errorOutput = { errorType, message };
        if (category === verifyTypes.SCHEMA) {
          errorOutput.category = verifyTypes.SCHEMA;
        }
        return errorOutput;
      };

      return ApplicationApi.SCIMPostVerify(payload)
        .then(({ data }) => {
          if (saveErrors) {
            const { failures = {} } = data;
            const failuresTypes = Object.keys(failures);
            const failuresCollection = failuresTypes
              .map(key => buildError(key, failures[key].message, payload.type));

            commit('SET_SCIM_TEST_CONNECTION_ERRORS', failuresCollection);

            if (isIdmTestConnectionFailure(failuresTypes)) {
              throw new Error('IdmTestConnectionFailure');
            }

            if (failuresTypes.includes('test_groups')) {
              commit('SET_SCIM_GROUPS_SUPPORT', false);
              commit('SET_SCIM_GROUPS_ENABLE', false);
            } else {
              commit('SET_SCIM_GROUPS_SUPPORT', true);
            }

            commit('SET_SCIM_TEST_CONNECTION_PASSED', true);
          }
          return data;
        })
        .catch((e) => {
          // Type verifyTypes.SCHEMA it's only for the preview functionality,
          if (type === verifyTypes.SCHEMA) {
            commit(
              'SET_SCIM_TEST_CONNECTION_ERRORS',
              [buildError('Preview Error', e.message, verifyTypes.SCHEMA)],
            );
          } else {
            commit('SET_SCIM_TEST_CONNECTION_PASSED', false);
            throw e;
          }
        });
    },

    deactivateScim({ commit, state }) {
      return ApplicationApi.SCIMDeleteProvisionActive(state.item.id)
        .then(() => {
          commit('RESET_SCIM');
        });
    },

    bindApplicationToUserGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.application,
        targetType: groupTypes.userGroup,
      });
    },

    resetCustomApiConfig({ commit }) {
      commit('RESET_IDM_CREDENTIALS');
      commit('RESET_OLD_CUSTOM_API_CONFIG');
      commit('SET_SCIM_TEST_CONNECTION_ERRORS');
      commit('SET_SCIM_TEST_CONFIG_ERRORS');
    },

    resetScimGenericData({ commit }) {
      commit('RESET_IDM_CREDENTIALS');
      commit('RESET_SCIM_GENERIC_DATA');
      commit('SET_SCIM_TEST_CONNECTION_ERRORS');
      commit('SET_SCIM_TEST_CONFIG_ERRORS');
    },

    deleteAppCustomLogo(context = { getters: { id: {} } }) {
      const { id } = context.getters;
      const { isDefaultLogoRestored, item } = context.state;

      if (!item.isCustomLogo || !isDefaultLogoRestored) return Promise.resolve(item);

      return ApplicationApi.deleteCustomLogo(id)
        .then(() => item)
        .catch((responseError) => {
          notify(error, responseError.message);
        });
    },

    async fetchItem(context = { getters: { id: {} } }) {
      const { id } = context.getters;
      const { templateId } = context.state;

      context.commit('RESET_MODEL'); // Reset state to default

      try {
        if (templateId) {
          return loadApplicationTemplate(templateId, context);
        }

        if (id) {
          const item = await loadApplicationItem(id, context);

          if (!isOIDC(item)) {
            await loadApplicationScim(item, context);
          }

          return item;
        }
      } catch (e) {
        if (e.message) { throw e; }
        throw new Error('There was a problem with loading the application');
      }
      return Promise.resolve();
    },

    removeScimTranslationRule({ commit }, id) {
      commit('REMOVE_SCIM_TRANSLATION_RULE', id);
    },

    resetState({ commit, dispatch }) {
      commit('RESET_MODEL');
      dispatch('setScimTestConnectionPassed', false);
    },

    async rotateOidcClientSecret(context) {
      const { commit, state } = context;
      const item = cloneDeep(state.item);

      try {
        if (!isOIDC(item)) {
          throw new Error('OIDC connection is not configured');
        }

        item.sso.oidc.clientSecret = 'rotate';
        const ssoResponse = await SsoApi.update(item._id, item.sso);
        const { clientId, clientSecret } = ssoResponse.oidc;
        commit('SET_OIDC_CLIENT_ID_AND_SECRET', { clientId, clientSecret });

        return ssoResponse;
      } catch (err) {
        if (err.message) {
          throw err;
        }
        throw new Error('There was a problem rotating the OIDC client secret');
      }
    },

    async saveItem(context) {
      const {
        commit,
        getters,
        state,
      } = context;

      const item = cloneDeep(state.item);

      let savedItem;

      if (getters.isItemBeingCreated) {
        savedItem = await ApplicationApi.create(item);

        if (isOIDC(item)) {
          try {
            const sso = await SsoApi.save(savedItem._id, item.sso);

            assignIn(savedItem, { sso });

            const { clientId, clientSecret } = sso.oidc;

            commit('SET_OIDC_CLIENT_ID_AND_SECRET', { clientId, clientSecret });
          } catch (e) {
            await ApplicationApi.delete(savedItem._id);

            throw Api.handleError(null, 'Unable to save OIDC connection', e.message);
          }
        }

        commit('SET_ITEM', { _id: savedItem.id, id: savedItem.id });
        commit('SET_ITEM_STATE', { update: true });
        commit('SET_UPDATE_MODEL_PROPERTIES');
      } else {
        savedItem = await ApplicationApi.update(item);

        if (isOIDC(item)) {
          const sso = await SsoApi.update(savedItem._id, item.sso);

          assignIn(savedItem, { sso });
        }
      }

      return savedItem;
    },

    /**
     * Exposing save state to session as action
     * @param {Object} state
     */
    saveAppStateForAuth({ state }, sessionStorageKey) {
      saveStateToSessionStorage(
        pickStatePropertiesForSessionStorage(state),
        sessionStorageKey,
      );
    },

    setApplicationLogo({ commit }, payload) {
      commit('SET_APPLICATION_LOGO', payload);
    },

    setAuthMethod({ commit }, method = BEARER) {
      commit('SET_AUTH_METHOD', method);
    },

    setCertOrKeyHasChanged({ commit }, certOrKeyHasChanged) {
      commit('SET_CERT_OR_KEY_HAS_CHANGED', certOrKeyHasChanged);
    },

    setConfig({ commit }, config) {
      commit('SET_CONFIG', config);
    },

    /**
     * Action to commit all parsed custom REST API integration config
     * @param {Object} commit
     * @param {Object} customRestApiConfig Object with config
     * @param {Object} customRestApiConfig.listUsersEndpointConfig,
     * @param {Array} customRestApiConfig.optionalAttributes,
     * @param {Array} customRestApiConfig.requiredAttributes,
     * @param {String} customRestApiConfig.userSchemaIdPath,
     */
    setCustomRestApiConfig({ commit, dispatch }, customRestApiConfig = {}) {
      /**
       * @todo Management of auth using header API key missing
       */
      const {
        listUsersEndpointConfig,
        optionalAttributes,
        requiredAttributes,
        userSchemaIdPath,
        inactiveStatus,
      } = customRestApiConfig;
      // Set connection type to Custom REST API
      commit('SET_SCIM_CONNECTION_TYPE', CUSTOM);

      // Set user schema ID path
      commit('SET_USER_SCHEMA_ID_PATH', userSchemaIdPath);

      // Set user schema inactive status object values
      commit('SET_USER_SCHEMA_INACTIVE_STATUS', inactiveStatus);

      // Set listUsers endpoint config
      commit('UPDATE_CUSTOM_IDM_ENDPOINT', listUsersEndpointConfig);

      // Set attribute mappings
      commit('SET_CUSTOM_IDM_MAPPING_ATTRIBUTES', requiredAttributes);

      optionalAttributes.forEach(optAttribute => {
        dispatch('addCustomApiOptionalMapping', optAttribute);
      });
    },

    /** Action to set back default SCIM translation rules */
    setDefaultScimTranslationRules({ dispatch }) {
      // NOTE: Add to array if more required TR come
      const requiredTrs = [cloneDeep(SCIM_PASSWORD_SYNC_TR)];
      requiredTrs.forEach(translationRule => {
        dispatch('addScimTranslationRule', translationRule);
      });
      dispatch('setScimGenericOldData');
    },

    setIsPrebuiltButCustomConfigured({ commit }, value) {
      commit('SET_IS_PREBUILT_BUT_CUSTOM_CONFIGURED', value);
    },

    setOidcClientIdAndSecret({ commit }, oidcData = {}) {
      commit('SET_OIDC_CLIENT_ID_AND_SECRET', oidcData);
    },

    setOldCustomApiConfig({ commit }) {
      commit('SET_OLD_CUSTOM_API_CONFIG');
    },

    setHeaderKeyPair({ commit }, headerConfig = {}) {
      commit('SET_HEADER_PAIR', headerConfig);
    },

    setIsDefaultLogoRestored({ commit }, payload) {
      commit('SET_IS_DEFAULT_LOGO_RESTORED', payload);
    },

    setItem({ commit }, { item } = {}) {
      commit('SET_ITEM', item);
      commit('SET_PREVIOUS_ITEM', item);
      return Promise.resolve(item);
    },

    setOAuthConfigValue({ commit }, payload) {
      commit('SET_OAUTH_CONFIG_VALUE', payload);
    },

    setScimConnectionType({ commit }, connectionType = SCIM) {
      commit('SET_SCIM_CONNECTION_TYPE', connectionType);
    },

    setScimGenericOldData({ commit }) {
      commit('SET_SCIM_GENERIC_OLD_DATA');
    },

    setScimGroupsEnable({ commit }, status = false) {
      commit('SET_SCIM_GROUPS_ENABLE', status);
    },

    setScimTestConnectionPassed({ commit }, status = false) {
      commit('SET_SCIM_TEST_CONNECTION_PASSED', status);
    },

    setIdmOauthConfigured({ commit }, status = false) {
      commit('SET_IDM_OAUTH_CONFIGURED', status);
    },

    setScimStatus({ commit }, status = false) {
      commit('SET_SCIM_STATUS', status);
    },

    setScimAuthState({ commit }, scimAuthState = '') {
      commit('SET_SCIM_AUTH_STATE', scimAuthState);
    },

    setScimNeedsCredentials({ commit }, scimNeedsCredentials = false) {
      commit('SET_SCIM_NEEDS_CREDENTIALS', scimNeedsCredentials);
    },

    setScimCredentials({ commit }, {
      urlValue = null,
      keyValue,
      scimVersion = 'v2',
    } = {}) {
      commit('SET_SCIM_VERSION', scimVersion);
      commit('SET_SCIM_TOKEN_KEY', keyValue);
      commit('SET_SCIM_VANITY_URL', urlValue);
    },

    setScimTokenKey({ commit }, keyValue) {
      commit('SET_SCIM_TOKEN_KEY', keyValue);
    },

    setScimTestUserEmail({ commit }, testUserEmailValue = '') {
      commit('SET_SCIM_TEST_USER_EMAIL', testUserEmailValue);
    },

    setScimVanityUrl({ commit }, urlValue) {
      commit('SET_SCIM_VANITY_URL', urlValue);
    },

    setScimScimVersion({ commit }, versionValue) {
      commit('SET_SCIM_VERSION', versionValue);
    },

    setSsoHidden({ commit }, hidden) {
      commit('SET_SSO_HIDDEN', hidden);
    },

    setTemplateId({ commit }, templateId) {
      commit('SET_TEMPLATE_ID', templateId);
    },

    updateScimTranslationRule({ commit }, payload = {}) {
      commit('UPDATE_SCIM_TRANSLATION_RULE', payload);
    },

    uploadAppCustomLogo(context = { getters: { id: {} } }) {
      const { id } = context.getters;
      const { appLogo, item } = context.state;
      if (!id || !appLogo) return Promise.resolve(item);

      const { file } = appLogo;
      return ApplicationApi.uploadCustomLogo({ id, file })
        .then(() => item)
        .catch((responseError) => {
          notify(error, responseError.message);
        });
    },

    updateConfigureScim({
      state, commit, dispatch, rootState,
    }, payload = {}) {
      const { applicationId = '' } = payload;
      const appId = state.item.id || applicationId;

      if (state.isScimActive) {
        dispatch('removeMalformedOptionalAttributes');
        const translationRulesPayload = formatScimTranslationRules(state.scimTranslationRules);

        const payloadForEndpoint = getConfigureSCIMPayload({
          rootState,
          state,
          translationRulesPayload,
        });

        return ApplicationApi.SCIMPutProvision(appId, payloadForEndpoint)
          .then(({ data }) => {
            commit('SET_SCIM_GROUPS_ENABLE', data.groups_enabled);
          })
          .catch((err) => {
            if (err.response) {
              const { failures = null } = err.response.data;
              if (failures) {
                setFailures({ commit }, failures);
              }
            }
            return Promise.reject(err);
          });
      }

      return Promise.resolve();
    },

    // CUSTOM REST API
    /**
     * Append new object to **customIdmMappingAttributes.optionalAttributes**
     * @param {*} commit
     * @param {Object} mapping
     * @param {String} mapping.spAttribute
     * @param {String} mapping.jcAttribute
     */
    addCustomApiOptionalMapping({ state, commit }, mapping = {}) {
      const {
        _id = Util.getUniqueId(),
        spAttribute = '',
        jcAttribute = '',
        direction = BIDIRECTIONAL,
      } = mapping;

      const { optionalAttributes } = state.customIdmMappingAttributes;

      const jcAttributeExists = optionalAttributes.find(
        ({ jcAttribute: existingJcAttribute }) => (jcAttribute !== '') && (existingJcAttribute === jcAttribute),
      );

      if (!jcAttributeExists) {
        mapping = {
          _id, jcAttribute, spAttribute, direction,
        };
        commit('ADD_CUSTOM_API_OPT_MAPPING', mapping);
      }
    },

    /**
     * @param {*} type notification type [sucess, error, warning, etc.]
     * @param {*} messageType valid key from ApplicationConstants.[ScimMessages|CustomOAuthMessages]
     */
    registerPendingIdmNotification({ commit }, { type, messageType }) {
      const notificationConfig = ScimMessages.get(messageType)
        || CustomOAuthMessages.get(messageType);

      commit(
        'ADD_PENDING_IDM_NOTIFICATION',
        { type, notificationConfig },
      );
    },

    removeCustomApiOptionalMapping({ commit }, _id) {
      commit('REMOVE_CUSTOM_API_OPT_MAPPING', _id);
    },

    removeMalformedOptionalAttributes({ commit, state }) {
      [...state.customIdmMappingAttributes.optionalAttributes].forEach(option => {
        if (!option.jcAttribute || !option.spAttribute) {
          commit('REMOVE_CUSTOM_API_OPT_MAPPING', option._id);
        }
      });
    },

    /**
     * Update object in **customIdmMappingAttributes.optionalAttributes**
     * @param {*} commit
     * @param {Object} updatedMapping
     * @param {String} updatedMapping.spAttribute
     * @param {String} updatedMapping.jcAttribute
     */
    updateCustomApiOptionalMapping({ commit }, updatedMapping = {}) {
      commit('UPDATE_CUSTOM_API_OPT_MAPPING', updatedMapping);
    },

    async populateCustomApiOptionalMapping({ dispatch, commit }, url) {
      const jcAttributesLength = userSchemaAttributes.length;
      const result = await axios.get(url);
      const jsonPaths = extractJsonPaths(result.data).slice(0, jcAttributesLength);
      commit('CLEAR_CUSTOM_API_OPT_MAPPING');
      jsonPaths.forEach((path) => {
        dispatch('addCustomApiOptionalMapping', { spAttribute: path });
      });
    },

    setUserSchemaIdPath({ commit }, jsonPath) {
      commit('SET_USER_SCHEMA_ID_PATH', jsonPath);
    },

    setUserSchemaInactiveStatus({ commit }, values) {
      commit('SET_USER_SCHEMA_INACTIVE_STATUS', values);
    },

    setCustomIdmAttributes({ commit }, payload) {
      commit('SET_CUSTOM_IDM_MAPPING_ATTRIBUTES', payload);
    },

    /**
     * @param {Object} payload
     * @param {String} payload.func (can't use function, it's a reserved word)
     * @param {String} payload.verb
     * @param {String} payload.url
     */
    updateCustomIdmEndpoint({ commit }, payload = {}) {
      commit('UPDATE_CUSTOM_IDM_ENDPOINT', payload);
    },

    updateCustomIdmOauthConfiguration({ commit, state }) {
      let payload = {};
      switch (state.authenticationMethod) {
        case SIMPLE_TOKEN:
          payload = {
            simple_token: {
              client_id: state.idmOauthConfig.clientId,
              client_secret: state.idmOauthConfig.clientSecret,
            },
          };
          break;

        case PASSWORD_TOKEN:
          payload = {
            password_token: {
              name_space: state.idmOauthConfig.clientNameSpace,
              username: state.idmOauthConfig.username,
              password: state.idmOauthConfig.password,
              host: state.idmOauthConfig.host,
              password_access_token_url: state.idmOauthConfig.passwordAccessTokenUrl,
            },
          };
          break;

        case OAUTH2:
        default:
          payload = {
            oauth: getPayloadForCustomOAuth(state).oauth,
          };
          break;
      }
      return ApplicationApi.idmPostReactivate(state.item.id, payload)
        .then(({ data }) => {
          commit('SET_SCIM_AUTH_STATE', data.state);
          if (data.type === 'oauth') {
            commit('SET_REDIRECT_LOCATION', data.location);
          }
        });
    },
  },

  getters: {
    ...model.getters,

    areScimCredentialsChanged(state) {
      const { scimGenericOldData, scimTokenKey } = state;
      const {
        scimVersion,
        scimVanityUrl,
        scimTestUserEmail,
      } = scimGenericOldData;

      const toCompare = {
        scimVersion,
        scimVanityUrl,
        scimTestUserEmail,
      };

      return !isMatch(state, toCompare) || scimTokenKey !== '';
    },

    customIdmOptionalAttributes(state) {
      return get(state, 'customIdmMappingAttributes.optionalAttributes');
    },

    // Returns if Custom Protocol was modified
    didCustomApiConfigChanged(state) {
      const EXCLUDED_PROP = 'idmOauthConfig';
      const { oldCustomApiConfig } = state;

      const oldConfig = {};
      const newConfig = {};
      Object.keys(oldCustomApiConfig).forEach((prop) => {
        if (prop !== EXCLUDED_PROP) {
          // For checking OAuth config change, use _didOauthConfigChanged_
          const isObj = isPlainObject(state[prop]);
          oldConfig[prop] = isObj ? cloneDeep(oldCustomApiConfig[prop]) : oldCustomApiConfig[prop];
          newConfig[prop] = isObj ? cloneDeep(state[prop]) : state[prop];
        }
      });

      // Remove optional attribute generated _ids
      // otherwise, objects will always differ
      oldConfig.customIdmMappingAttributes.optionalAttributes
        .forEach(optAttr => delete optAttr._id);
      newConfig.customIdmMappingAttributes.optionalAttributes
        .forEach(optAttr => delete optAttr._id);

      return !isEqual(newConfig, oldConfig);
    },

    didOauthConfigChanged(state) {
      const {
        idmOauthConfig: currentOAuthConfig,
        oldCustomApiConfig: {
          idmOauthConfig: oldOAuthConfig,
        },
      } = state;

      return !isMatch(currentOAuthConfig, oldOAuthConfig);
    },

    didTranslationRulesChanged(state) {
      const { scimGenericOldData } = state;
      const { scimTranslationRules: oldTrs } = scimGenericOldData;
      const { scimTranslationRules: newTrs } = state;

      let didTrsChange = true;

      if (oldTrs.length === newTrs.length) {
        didTrsChange = !isMatch(oldTrs, newTrs);
      }

      return didTrsChange;
    },

    displayName(state) {
      return get(state, 'item.displayName', '');
    },

    isJitEnabled(state) {
      const { item, template } = state;
      if (!template.jit) return false;

      const { databaseAttributes, constantAttributes } = item.config;
      const { jit: jitConfiguration = {} } = template;
      const { attributes: jitRequiredAttributes = [] } = jitConfiguration;

      const instanceAttributesDictionary = item.databaseAttributes
        .concat(databaseAttributes.value)
        .concat(constantAttributes.value)
        .reduce((dictionary, attr) => ({
          [attr.name]: true,
          ...dictionary,
        }), {});

      return !jitRequiredAttributes.some(attr => (attr.required || attr.required === undefined)
        && !instanceAttributesDictionary[attr.name]);
    },

    isScimGenericConnectorChanged(state, getters) {
      const { scimGroupsEnabled } = state.scimGenericOldData;

      return !isMatch(state, scimGroupsEnabled) || getters.didTranslationRulesChanged;
    },

    isScimGroupsEnabledChanged(state) {
      const { scimGenericOldData, scimGroupsEnabled } = state;
      return scimGenericOldData.scimGroupsEnabled !== scimGroupsEnabled;
    },

    logoUrl(state) {
      return get(state, 'item.logo.url') || get(state, 'template.logo.url');
    },

    scim(state) {
      return get(state, 'template.provision');
    },

    ssoHidden(state) {
      return get(state, 'item.sso.hidden');
    },

    templateConstantAttributesValue(state) {
      const { config } = state.template;
      const constantAttributes = (config.constantAttributes && config.constantAttributes.value)
        || [];

      return constantAttributes.reduce((dictionary, attribute) => ({
        [attribute.name]: attribute,
        ...dictionary,
      }), {});
    },

    templateLogoUrl(state) {
      return get(state, 'template.logo.url');
    },
  },

  mutations: {
    ...model.mutations,
    ADD_CUSTOM_API_OPT_MAPPING(state, mapping = {}) {
      const { optionalAttributes } = state.customIdmMappingAttributes;
      optionalAttributes.push(mapping);
    },

    ADD_PENDING_IDM_NOTIFICATION(state, notificationConfig = {}) {
      state.pendingIdmNotifications.push(notificationConfig);
    },

    REMOVE_CUSTOM_API_OPT_MAPPING(state, _id) {
      const { optionalAttributes } = state.customIdmMappingAttributes;

      const deleteTargetIndex = optionalAttributes.findIndex(mapping => mapping._id === _id);

      optionalAttributes.splice(deleteTargetIndex, 1);
    },

    CLEAR_CUSTOM_API_OPT_MAPPING(state) {
      state.customIdmMappingAttributes.optionalAttributes = [];
    },

    CLEAR_PENDING_IDM_NOTIFICATIONS(state) {
      state.pendingIdmNotifications = [];
    },

    UPDATE_CUSTOM_API_OPT_MAPPING(state, updatedMapping = {}) {
      const { _id } = updatedMapping;
      const { optionalAttributes } = state.customIdmMappingAttributes;
      const targetMappingIndex = optionalAttributes.findIndex(mapping => mapping._id === _id);

      Vue.set(optionalAttributes, targetMappingIndex, updatedMapping);
    },

    ADD_DEFAULT_PROPERTY_VALUES(state) {
      const { item } = state;

      if (!!item.name && !item.isBookmark && !isOIDC(item)) {
        const newConfig = cloneDeep(item.config) || {};

        if (newConfig.idpCertificate) newConfig.idpCertificate.visible = false;
        if (newConfig.idpPrivateKey) newConfig.idpPrivateKey.visible = false;

        const databaseAttributes = extend({}, newConfig.databaseAttributes || {
          value: [],
        }, defaultDatabaseAttributes);

        const constantAttributes = extend({}, newConfig.constantAttributes || {
          value: [],
        }, defaultConstantAttributes);

        const includeGroups = extend({}, newConfig.includeGroups || {
          position: 5,
          required: true,
          value: false,
        }, defaultIncludeGroups);

        const groupsAttributeName = extend({}, newConfig.groupsAttributeName || {
          position: 6,
          required: true,
          value: '',
        }, defaultGroupAttributes);

        extend(newConfig, {
          constantAttributes,
          databaseAttributes,
          groupsAttributeName,
          includeGroups,
        });

        state.item.config = newConfig;
        state.item.databaseAttributes = state.item.databaseAttributes || [];
      }
    },

    /**
     * Append _payload_ object into translation rules array
     * @param state
     * @param {Object} payload
     * @param {string} payload.id
     * @param {string} payload.target
     * @param {template} payload.template
     * @param {boolean} payload.exclude
    */
    ADD_SCIM_TRANSLATION_RULE(state, payload) {
      const {
        _id = Util.getUniqueId(),
        target,
        template,
        exclude,
        required = false,
        readOnly = false,
      } = payload;

      // Convert this into Array.includes if more readonly translation rules appear
      const isRequiredTr = target === PASSWORD_TR;

      const newTr = {
        _id,
        target,
        template,
        exclude,
        readOnly,
        required,
      };

      if (isRequiredTr) {
        newTr.readOnly = true;
        newTr.required = true;
        // Required translation rules go first. Order defined by
        // @linkcode loadApplicationScim -> const requiredTrs
        state.scimTranslationRules.unshift(newTr);
      } else {
        state.scimTranslationRules.push(newTr);
      }
    },

    REMOVE_SCIM_TRANSLATION_RULE(state, id) {
      const deleteTargetIndex = state.scimTranslationRules.findIndex(rule => rule._id === id);
      state.scimTranslationRules.splice(deleteTargetIndex, 1);
    },

    RESET_MODEL(state) {
      Object.entries(getDefaultState()).forEach(([key, value]) => Vue.set(state, key, value));
    },

    RESET_SCIM(state) {
      Object.entries(getScimDefault()).forEach(([key, value]) => Vue.set(state, key, value));
    },

    SET_APPLICATION_LOGO(state, payload) {
      extend(state, { appLogo: payload });
    },

    SET_AUTH_METHOD(state, method = BEARER) {
      state.authenticationMethod = method;
    },

    SET_CERT_OR_KEY_HAS_CHANGED(state, certOrKeyHasChanged) {
      state.certOrKeyHasChanged = certOrKeyHasChanged;
    },

    SET_CONFIG(state, config) {
      state.item.config = { ...config };
    },

    SET_OIDC_CLIENT_ID_AND_SECRET(state, oidcData = {}) {
      const { clientId, clientSecret } = oidcData;

      Vue.set(state, 'oidcConfigResponse', {
        clientId,
        clientSecret,
      });
    },

    SET_OLD_CUSTOM_API_CONFIG(state) {
      const { oldCustomApiConfig } = state;

      Object.keys(oldCustomApiConfig).forEach((prop) => {
        Vue.set(oldCustomApiConfig, prop, cloneDeep(state[prop]));
      });
    },

    RESET_OLD_CUSTOM_API_CONFIG(state) {
      const { oldCustomApiConfig } = state;

      Object.keys(oldCustomApiConfig).forEach(prop => {
        Vue.set(state, prop, cloneDeep(oldCustomApiConfig[prop]));
      });
    },

    SET_UPDATE_MODEL_PROPERTIES(state) {
      const { item } = state;

      const { config } = item;
      if (config && config.spEntityId) {
        state.spEntityId = config.spEntityId.value;
      }
    },

    SET_HEADER_PAIR(state, headerConfig = {}) {
      const { name, value } = headerConfig;
      state.headerKeyPair = {
        name,
        value,
      };
    },

    SET_IS_BOOKMARK(state) {
      state.item.isBookmark = (state.item.sso.type === 'bookmark');
    },

    SET_IS_CUSTOM_LOGO(state) {
      const { item, template } = state;
      state.item.isCustomLogo = !!item.logo.url && item.logo.url !== template.logo.url;
    },

    SET_IS_DEFAULT_LOGO_RESTORED(state, payload) {
      extend(state, { isDefaultLogoRestored: payload });
    },

    SET_IS_PREBUILT_BUT_CUSTOM_CONFIGURED(state, value) {
      state.isPrebuiltButCustomConfigured = value;
    },

    SET_OAUTH_CONFIG_VALUE(state, payload) {
      state.idmOauthConfig = payload;
    },

    SET_CLIENT_ID(state, clientId) {
      state.clientId = clientId;
    },

    SET_SCIM_TEST_USER_EMAIL(state, testUserEmailValue = '') {
      state.scimTestUserEmail = testUserEmailValue;
    },

    SET_REDIRECT_LOCATION(state, payload) {
      state.scimRedirectLocation = payload;
    },

    INIT_SCIM_PROPERTIES(state) {
      const { template } = state;
      const { provision = false } = template;

      state.isScimSupported = !!template.provision;
      state.scimGroupsSupport = provision && provision.groups_supported;
      state.scimGroupsEnabled = !!state.scimGroupsSupport;
    },

    SET_SCIM_CONNECTION_TYPE(state, connectionType = SCIM) {
      state.scimConnectionType = connectionType;
    },

    SET_SCIM_GROUPS_ENABLE(state, status = false) {
      state.scimGroupsEnabled = status;
    },

    SET_SCIM_GROUPS_SUPPORT(state, isSupported = false) {
      state.scimGroupsSupport = isSupported;
    },

    SET_SCIM_TEST_CONNECTION_PASSED(state, status = false) {
      state.scimIsTestConnectionPassed = status;
    },

    SET_IDM_OAUTH_CONFIGURED(state, status = false) {
      state.isIdmOauthConfigured = status;
    },

    SET_SCIM_STATUS(state, status = false) {
      state.isScimActive = status;
    },

    SET_SCIM_AUTH_STATE(state, payload) {
      state.scimAuthState = payload;
    },

    SET_SCIM_NEEDS_CREDENTIALS(state, payload) {
      state.scimNeedsCredentials = payload;
    },

    SET_SCIM_NEEDS_VANITY_URL(state, payload) {
      state.scimNeedsVanityUrl = payload;
    },

    SET_SCIM_VERSION(state, scimVersion = 'v2') {
      state.scimVersion = scimVersion;
    },

    SET_SCIM_TOKEN_KEY(state, keyValue) {
      state.scimTokenKey = keyValue;
    },

    SET_SCIM_VANITY_URL(state, urlValue = null) {
      state.scimVanityUrl = urlValue;
    },

    SET_SCIM_GENERIC_OLD_DATA(state) {
      const {
        scimGroupsEnabled,
        scimVanityUrl,
        scimVersion,
        scimIsTestConnectionPassed,
        scimTranslationRules,
        scimTestUserEmail,
      } = state;
      state.scimGenericOldData = {
        scimGroupsEnabled,
        scimVanityUrl,
        scimVersion,
        scimIsTestConnectionPassed,
        scimTestUserEmail,
        scimTranslationRules: cloneDeep(scimTranslationRules),
      };
    },

    RESET_SCIM_GENERIC_DATA(state) {
      const {
        scimGroupsEnabled,
        scimVersion,
        scimIsTestConnectionPassed,
        scimTranslationRules,
      } = state.scimGenericOldData;
      state.scimGroupsEnabled = scimGroupsEnabled;
      state.scimVersion = scimVersion;
      state.scimIsTestConnectionPassed = scimIsTestConnectionPassed;
      state.scimTranslationRules = cloneDeep(scimTranslationRules);
    },

    RESET_IDM_CREDENTIALS(state) {
      const { scimVanityUrl } = state.scimGenericOldData;
      state.scimTokenKey = '';
      state.scimVanityUrl = scimVanityUrl;
    },

    SET_SCIM_TRANSLATION_RULES(state, payload = []) {
      Vue.set(state, 'scimTranslationRules', payload);
    },

    SET_SCIM_TEST_CONNECTION_ERRORS(state, errors = []) {
      state.scimTestConnectionErrors = errors;
    },

    SET_SCIM_TEST_CONFIG_ERRORS(state, errors = []) {
      state.scimTestConfigErrors = errors;
    },

    SET_SSO_HIDDEN(state, hidden) {
      state.item.sso.hidden = hidden;
    },

    SET_SSO_URL(state) {
      state.item.ssoUrl = state.item.isBookmark || isOIDC(state.item) ? '' : state.item.ssoUrl;
    },

    SET_TEMPLATE(state, template) {
      state.template = template;
    },

    SET_TEMPLATE_ID(state, templateId) {
      state.templateId = templateId;
    },

    // CUSTOM REST API
    SET_USER_SCHEMA_ID_PATH(state, jsonPath) {
      state.userSchemaIdPath = jsonPath;
    },

    SET_USER_SCHEMA_INACTIVE_STATUS(state, newStatus) {
      state.inactiveStatus.path = newStatus.path;
      state.inactiveStatus.values = newStatus.values;
    },

    SET_CUSTOM_IDM_MAPPING_ATTRIBUTES(state, payload = {}) {
      const keys = Object.keys(state.customIdmMappingAttributes);
      keys.forEach((key) => {
        if (key in payload) {
          state.customIdmMappingAttributes[key] = payload[key];
        }
      });
    },

    SET_IDM_NEEDS_SIMPLE_TOKEN(state, payload) {
      state.idmNeedsSimpleToken = payload;
    },

    SET_IDM_NEEDS_PASSWORD_TOKEN(state, payload) {
      state.idmNeedsPasswordToken = payload;
    },

    SPLIT_SSO_URL(state) {
      const { ssoUrl } = state.item;
      if (!(typeof ssoUrl === 'string')) return;

      const fileNameIndex = ssoUrl.lastIndexOf('/') + 1;
      if (fileNameIndex < 0) return;

      state.item.ssoDirName = ssoUrl.slice(0, fileNameIndex);

      if (state.item.ssoFileName === undefined) {
        Vue.set(state.item, 'ssoFileName', ssoUrl.slice(fileNameIndex));
      }
    },

    UPDATE_CUSTOM_IDM_ENDPOINT(state, payload = {}) {
      const {
        func,
        verb,
        path,
        pagination = {},
        resourcesPath = {},
        totalCount = {},
      } = payload;

      const { customIdmEndpointsConfig } = state;
      customIdmEndpointsConfig[func] = {
        verb,
        path,
        pagination,
        resourcesPath,
        totalCount,
      };
      const endpoint = customIdmEndpointsConfig[func];
      Object.keys(pagination).forEach((key) => {
        endpoint.pagination[key] = String(pagination[key]);
      });
    },

    /**
     * Update translation rule object in translation rules array
     * @param state
     * @param {Object} payload
     * @param {string} payload.id - Specifies element to update
     * @param {string} payload.target
     * @param {template} payload.template
     * @param {boolean} payload.exclude
    */
    UPDATE_SCIM_TRANSLATION_RULE(state, payload) {
      /** Translation rule object structure:
       * {
       *    "target": "targetValue",
       *    ["template": "actualValue" | "exclude": boolean]
       * }
       */
      const {
        _id, target, template = 'Undefined template received', exclude,
      } = payload;

      const lookupTranslationRule = state.scimTranslationRules.find(rule => (rule._id === _id));

      lookupTranslationRule.target = target;
      lookupTranslationRule.exclude = exclude;
      lookupTranslationRule.template = template;
    },
  },
};
